import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  EmployeeQuestionnaireStatusesEnum,
  PLATFORM_PERMISSIONS,
} from '@thinknet-gmbh/platform-common';

import { Icon, ICONS, PLATFORM_THEME_COLORS, Popover } from '@thinknet-gmbh/platform-common-ui';

import { useUserCanManageDocument } from 'src/hooks/user-can-manage-document.hook';
import { useGetCurrentMemberAccount } from 'src/hooks/accounts/get-current-member-account.hook';
import { useResendEmployeeQuestionnaireLink } from 'src/hooks/employee-questionnaire/resend-employee-questionnaire.hook';


import {
  ALLOW_ACTION_QUESTIONNAIRE_STATUSES,
  QUESTIONNAIRE_MODALS,
} from '../../open-questionnaires-list.constants';

import { PopoverEmptyWrapper } from '../../open-questionnaires-list.styles';

import { IOpenQuestionnairesTableRowProps } from './open-questionnaires-list-table.interfaces';
import { ROUTES } from 'src/components/app/app-routes.constants';
import { EMPLOYEE_PAGES } from '../../../../employee.constants';


const OpenQuestionnairesTableRowPopover = ({
  record,
  handleModals,
}: IOpenQuestionnairesTableRowProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['employee', 'auth']);
  const { member } = useGetCurrentMemberAccount();

  const { canDelete, canEdit } = useUserCanManageDocument({
    createdBy: member?.account?._id,
    platformPermission: PLATFORM_PERMISSIONS.memberManagement,
  });

  const resendEmployeeQuestionnaireLink = useResendEmployeeQuestionnaireLink({});

  const isHasAccessToChange =
    record?.responsiblePersonsIds?.includes(member?._id) &&
    ALLOW_ACTION_QUESTIONNAIRE_STATUSES.includes(record.status);

  const isHasAccessToCreate = EmployeeQuestionnaireStatusesEnum.received === record.status && canEdit;
  const isHasAccessToSend =
    [EmployeeQuestionnaireStatusesEnum.created, EmployeeQuestionnaireStatusesEnum.sent].includes(
      record?.status
    ) && canEdit;

  const resendData = isHasAccessToSend
    ? [
        {
          label: t('employee:questionnaire.labels.listActions.resend'),
          key: ICONS.send,
          icon: ICONS.send,
          handler: () =>
            resendEmployeeQuestionnaireLink({
              variables: {
                id: record._id,
              },
            }),
        },
      ]
    : [];

  const elements = isHasAccessToChange
    ? [
        ...resendData,
        {
          label: t('employee:questionnaire.labels.listActions.create'),
          key: ICONS.foodMenu,
          icon: ICONS.editAlt,
          color: isHasAccessToCreate
            ? PLATFORM_THEME_COLORS.primary.black
            : PLATFORM_THEME_COLORS.grayVariations.murky,
          handler: () => {
            if (isHasAccessToCreate) {
              navigate(`${ROUTES.employee}/${EMPLOYEE_PAGES.acceptQuestionnaire}?id=${record._id}`)
            }
          },
        },
        {
          label: t('employee:questionnaire.labels.listActions.delete'),
          icon: ICONS.trash,
          key: ICONS.trash,
          color: canDelete
            ? PLATFORM_THEME_COLORS.tone.red
            : PLATFORM_THEME_COLORS.grayVariations.murky,
          handler: () => {
            if (canDelete) {
              handleModals(QUESTIONNAIRE_MODALS.delete, record);
            }
          },
        },
      ]
    : [
        {
          label: 'Dont have access',
          key: ICONS.trash,
          color: PLATFORM_THEME_COLORS.grayVariations.murky,
        },
      ];

  const Element = isHasAccessToChange ? Popover : PopoverEmptyWrapper;

  return (
    <Element elements={elements}>
      <Icon
        icon={ICONS.dotsVerticalRounded}
        size={22}
        rightMargin={false}
        color={
          isHasAccessToChange
            ? PLATFORM_THEME_COLORS.primary.grey
            : PLATFORM_THEME_COLORS.grayVariations.murky
        }
      />
    </Element>
  );
};

export default OpenQuestionnairesTableRowPopover;
